import React from 'react';

import { useIntl } from 'react-intl';

import ZonePricingPlanFormModal from 'src/features/zone/components/form/ZonePricingPlanFormModal';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { ZonePricingPlan } from 'src/features/zone/utils/types';
import { LIST_ZONE_PRICING_PLANS_RESOURCE } from 'src/features/zone/api/useListZonePricingPlans';
import { useUpdateZonePricingPlan } from 'src/features/zone/api/useUpdateZonePricingPland';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface ZoneFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedZonePricingPlan: ZonePricingPlan;
    children?: React.ReactNode;
}

function UpdateZoneFormModal<TData>({
    className,
    semantics,
    children,
    selectedZonePricingPlan,
}: ZoneFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateZonePricingPlan({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ZONE_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(zoneMessages.updateZoneSuccess),
                description: formatMessage(zoneMessages.updateZoneSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <ZonePricingPlanFormModal
            selectedZonePricingPlan={selectedZonePricingPlan}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, planId: selectedZonePricingPlan.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.editPricingPlanSubtitle, {
                entry: '',
            })}
        >
            {children}
        </ZonePricingPlanFormModal>
    );
}

export default UpdateZoneFormModal;
